// One door to public-api: /api/v2, session cookies, CSRF on writes.

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

function csrf(): string {
  const c = document.cookie.split('; ').find((x) => x.startsWith('sc_csrf='));
  return c ? decodeURIComponent(c.slice(8)) : '';
}

function message(data: unknown, status: number): string {
  if (data && typeof data === 'object' && 'detail' in data) {
    const d = (data as { detail: unknown }).detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      if (typeof o.message === 'string') return o.message;
      if (typeof o.detail === 'string') return o.detail;
    }
    if (Array.isArray(d) && d[0] && typeof d[0].msg === 'string') return d[0].msg;
  }
  return status >= 500 ? 'Something went wrong on our side. Please try again.' : `Request failed (${status})`;
}

type Options = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** the call checks a password or code: v1 answers a wrong one with 401, which is not a signed-out browser */
  credential?: boolean;
};

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? (opts.body === undefined ? 'GET' : 'POST');
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Scamp-Web': '1' };
  if (method !== 'GET') headers['X-CSRF-Token'] = csrf();
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch('/api/v2' + path, {
    method,
    headers,
    credentials: 'same-origin',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    if (res.status === 401 && !opts.credential && !path.startsWith('/session/')) window.dispatchEvent(new Event('scamp:signed-out'));
    throw new ApiError(res.status, message(data, res.status), data);
  }
  return data as T;
}
